export interface LookerDashboardDefinition {
  embedPath: string;
  defaultFilters: Record<string, string>;
}

/** Default date range for LookML dashboards — shorter window for faster first load. */
export const LOOKER_DEFAULT_DATE_INTERVAL = '7 days';

/** MI organization IDs allowed to view BYP Looker dashboards (routes 27/28). */
export const BYP_LOOKER_ORGANIZATION_IDS: readonly number[] = [149];

/** LookML dashboards embedded by URL (not numeric Looker ID). */
export const LOOKER_DASHBOARD_DEFINITIONS: Record<string, LookerDashboardDefinition> = {
  executive: {
    embedPath: '/embed/dashboards/viba_statistics_new::executive_dashboard',
    defaultFilters: {
      date_interval: LOOKER_DEFAULT_DATE_INTERVAL,
      location: '',
      country: '',
      city: '',
    },
  },
  interactions: {
    embedPath: '/embed/dashboards/viba_statistics_new::interactions_dashboard',
    defaultFilters: {
      date_interval: LOOKER_DEFAULT_DATE_INTERVAL,
      location: '',
      country: '',
      city: '',
      event_type: 'checkoutstart',
    },
  },
};

/** Legacy numeric routes still used in sidenav/navbar. */
export const LOOKER_DASHBOARD_ALIASES: Record<string, string> = {
  '27': 'executive',
  '28': 'interactions',
};

export function resolveLookerDashboardKey(dashboardRouteKey: string): string {
  return LOOKER_DASHBOARD_ALIASES[dashboardRouteKey] ?? dashboardRouteKey;
}

export function buildLookerDashboardEmbedUrl(
  definition: LookerDashboardDefinition,
  queryParams: Record<string, string> = {}
): string {
  const filters = { ...definition.defaultFilters, ...queryParams };
  const searchParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    searchParams.set(key, value ?? '');
  });

  const query = searchParams.toString();
  return query ? `${definition.embedPath}?${query}` : definition.embedPath;
}

export function getLookerDashboardEmbedUrl(
  dashboardRouteKey: string,
  queryParams: Record<string, string> = {}
): string | undefined {
  const key = resolveLookerDashboardKey(dashboardRouteKey);
  const definition = LOOKER_DASHBOARD_DEFINITIONS[key];

  return definition
    ? buildLookerDashboardEmbedUrl(definition, queryParams)
    : undefined;
}

export function isLookmlLookerDashboard(dashboardRouteKey: string): boolean {
  return !!getLookerDashboardEmbedUrl(dashboardRouteKey);
}

/** Route IDs in sidenav/navbar for BYP-only Looker dashboards. */
export const BYP_LOOKER_DASHBOARD_ROUTES: readonly string[] = ['27', '28'];

/** Standard Looker dashboards shown to all tenants except BYP. */
export const STANDARD_LOOKER_DASHBOARD_ROUTES: readonly string[] = ['11', '13', '15'];

export function isBypLookerDashboardRoute(dashboardRouteKey: string): boolean {
  return BYP_LOOKER_DASHBOARD_ROUTES.includes(dashboardRouteKey);
}

export function isStandardLookerDashboardRoute(dashboardRouteKey: string): boolean {
  return STANDARD_LOOKER_DASHBOARD_ROUTES.includes(dashboardRouteKey);
}

export function isBypLookerOrganization(
  orgId: number | string | null | undefined,
): boolean {
  const normalizedOrgId = Number(orgId);
  return !!normalizedOrgId && BYP_LOOKER_ORGANIZATION_IDS.includes(normalizedOrgId);
}
